import * as React from 'react'
import { useEffect, useState } from 'react'
import { styled } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import MuiDrawer from '@mui/material/Drawer'
import MuiAppBar from '@mui/material/AppBar'
import { Box, Divider, IconButton, Switch, Toolbar } from '@mui/material'
import { CancelOutlined, ChevronLeft } from '@mui/icons-material'
import Language from '../../components/Language/Language'
import { Body, Logout, Menu, Mobile, NavTitle, ProfileIcon, Support } from '../Layout/partial'
import MenuIcon from '@mui/icons-material/Menu'
import { useLocation, useParams } from 'react-router-dom/dist'
import { useDispatch } from 'react-redux'
import {
    updateAnalysisCodeData,
    updateAnalysisDimensionData,
    updateAnalysisStructureData,
    updateAttachmentCount,
    updateExcelImportData,
    updateGroupData,
    updateInvoiceMain,
    updateOrderData,
    updateUserData,
    updateVendorData,
} from '../../store/slice/mainToCardSlice'
import DeleteDialog from '../../components/Dialog/DeleteDialog'
import CreateDialog from '../../components/Dialog/CreateDialog'
import DataGridDialog from '../../components/Dialog/DataGridDialog'
import ApproveDialog from '../../components/Dialog/ApproveDialog'
import LogDialog from '../../components/Dialog/LogDialog'
import HoldDialog from '../../components/Dialog/HoldDialog'
import RejectDialog from '../../components/Dialog/RejectDialog'
import SendToApproveDialog from '../../components/Dialog/SendToApproveDialog'
import { updateRequestData, updateSelectedMaterialData } from '../../store/slice/persistSlice'
import { useGetMenuWithPrivelegesQuery } from "../../store/api/menuApi";
import Loading from "../../components/Loading/Loading";
import Snowflakes from 'magic-snowflakes';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SevereColdIcon from '@mui/icons-material/SevereCold';
import { setStep } from '../../store/slice/buttonSlice'
import { offCreateSup, offReadSup, offReviseSup, onIndividual } from '../../store/slice/otherSlice'
import Help from './Help'
import Faq from './Faq'

const drawerWidth = 250

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(['width', 'margin'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}))

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    '& .MuiDrawer-paper': {
        position: 'relative',
        whiteSpace: 'nowrap',
        width: drawerWidth,
        transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        boxSizing: 'border-box',
        ...(!open && {
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
            }),
            width: theme.spacing(7),
            [theme.breakpoints.up('sm')]: {
                width: theme.spacing(9),
            },
        }),
    },
}))

function SharedLayout({ mode, setMode }) {
    const { data, isLoading } = useGetMenuWithPrivelegesQuery()
    const currentMenu = data?.data?.find(item => window.location.href.includes(item.url))
    const hasAccess = data && currentMenu?.readAccess || window.location.pathname == '/' || window.location.href.includes('/user/') || window.location.href.includes('/select-request')
    const [open, setOpen] = useState(false)

    const toggleDrawer = () => {
        setOpen(!open)
    }

    const handleModeChange = (e) => {
        const value = e.target.checked
        if (value) {
            setMode('dark')
            localStorage.setItem('mode', 'dark')
        } else {
            setMode('light')
            localStorage.setItem('mode', 'light')
        }
    }

    const startSnowfall = () => {
        const snowflakes = new Snowflakes();

        if (typeof snowflakes.destroy === 'function') {
            setTimeout(() => {
                snowflakes.destroy();
            }, 5000);
        }
    }


    const [snowflakes, setSnowflakes] = useState(null);
    const [snowMode, setSnowMode] = useState(null);

    useEffect(() => {
        const startSnowfall = () => {
            const snowflakesInstance = new Snowflakes();
            setSnowflakes(snowflakesInstance);
        };

        const stopSnowfall = () => {
            if (snowflakes && typeof snowflakes.destroy === 'function') {
                snowflakes.destroy();
                setSnowflakes(null);
            }
        };

        if (snowMode === 'snow') {
            startSnowfall();
        } else {
            stopSnowfall();
        }
    }, [snowMode]);

    const handleSnowModeChange = () => {
        setSnowMode((prevMode) => (prevMode === 'snow' ? 'unSnow' : 'snow'));
    };


    useEffect(() => {
        const mode = localStorage.getItem('mode')
        if (mode) {
            setMode(mode)
        }
    }, [setMode, mode])

    // Url Change
    const dispatch = useDispatch()
    const url = useLocation().pathname
    useEffect(() => {
        dispatch(updateUserData([]))
        dispatch(updateVendorData([]))
        dispatch(updateGroupData([]))
        dispatch(updateOrderData([]))
        dispatch(updateAnalysisCodeData([]))
        dispatch(updateAnalysisDimensionData([]))
        dispatch(updateAnalysisStructureData([]))
        dispatch(updateRequestData([]))
        dispatch(updateAttachmentCount(0))
        dispatch(updateSelectedMaterialData(null))
        dispatch(updateExcelImportData([]))
        dispatch(updateInvoiceMain([]))
        dispatch(setStep(0))
        // dispatch(updateCopyRequestData({}))
    }, [url, dispatch])


    const { dataId } = useParams();
    useEffect(() => {
        if (url !== '/supplier-evaluation') {
            dispatch(offCreateSup())
            dispatch(offReviseSup())
            dispatch(offReadSup())
        }
    }, [dispatch, url])

    useEffect(() => {
        if (url === `/supplier-evaluation/${dataId}`) {
            dispatch(offCreateSup())
            dispatch(offReviseSup())
            dispatch(offReadSup())
            dispatch(onIndividual(false))
        }
    }, [dispatch, url, dataId])

    useEffect(() => {
        if (!hasAccess && data) {
            window.location.href = '/404'
        }
    }, [data]);

    return (
        isLoading && !hasAccess ? <Loading /> : hasAccess && <Box sx={{ display: 'flex' }}>
            <CssBaseline />
            <DeleteDialog />
            <CreateDialog />
            <DataGridDialog />
            <ApproveDialog />
            <HoldDialog />
            <RejectDialog />
            <LogDialog />
            <SendToApproveDialog />
            {/* sx={{ bgcolor: "#4808C5" }} */}
            <AppBar position="absolute">
                <Toolbar
                    sx={{
                        pr: '24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <IconButton
                        edge="start"
                        color="inherit"
                        aria-label="open drawer"
                        onClick={toggleDrawer}
                        sx={{
                            marginRight: '36px',
                            ...(open && { display: 'none' }),
                            display: { xs: 'none', sm: 'none', md: 'block' },
                        }}
                    >
                        <MenuIcon />
                    </IconButton>

                    <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'none', md: 'block' } }}>
                        <NavTitle />
                    </Box>

                    <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'none', md: 'block' } }}>{/* <Search /> */}</Box>

                    <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                        {/* <Chat />
                        <Notification /> */}
                        {/* <Support /> */}
                    </Box>
                    <Box sx={{ display: { xs: 'block', sm: 'block', md: 'none' } }}>
                        <Mobile />
                    </Box>



                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Faq />
                        <Help />
                        <Language color="white" />
                        <ProfileIcon />
                        <Logout />
                        {/*<Switch onChange={handleModeChange} checked={mode === 'dark'}/>*/}

                        {/* <IconButton onClick={handleSnowModeChange}>
                            {snowMode === 'snow' ? <SevereColdIcon color="disabled" /> : <AcUnitIcon style={{ color: 'white' }} />}
                        </IconButton> */}

                    </Box>


                </Toolbar>
            </AppBar>

            <Drawer variant="permanent" open={open} sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                <Toolbar sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: [1],
                }}>
                    <IconButton onClick={toggleDrawer}>
                        <ChevronLeft />
                    </IconButton>
                </Toolbar>
                <Divider />

                <Menu />
            </Drawer>

            <Body />
        </Box>
    )
}

export default SharedLayout